import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { IGetPatientsParams, IPatientsPageable } from '~/interfaces'
import { HttpStatusCode, NotFoundError, UnexpectedError } from '~/protocols/http'
import { fetchPatients, message } from '~/services'
import { hasObjectValue } from '~/utils'

const UNIQUE_KEY = 'patients'

type ErrorType = Error | NotFoundError | UnexpectedError

export const useFetchPatients = (
  fetchParams: IGetPatientsParams | null
): UseQueryResult<IPatientsPageable, ErrorType> => {
  const hasFetchParams = hasObjectValue({ ...fetchParams })

  const fetcher = async () => {
    if (!fetchParams) throw new Error('fetchParams is required')

    try {
      const response = await fetchPatients(fetchParams)
      const isResponseEmpty = response.data.length === 0

      if (isResponseEmpty) {
        throw new NotFoundError(message('patient.not_found'))
      }

      return response
    } catch (error: any) {
      if (typeof error === 'string') throw new Error(error)
      if (error?.status === HttpStatusCode.notFound || error instanceof NotFoundError)
        throw new NotFoundError(message('patient.not_found'))

      throw new UnexpectedError(message('patient.unexpected_error'))
    }
  }

  return useQuery<IPatientsPageable, ErrorType>([UNIQUE_KEY, fetchParams], fetcher, {
    enabled: hasFetchParams,
    cacheTime: Infinity,
    staleTime: Infinity
  })
}
